import React, {
  ReactNode,
  RefObject,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  UserDTO,
} from 'dtos';
import {
  ToDo,
} from 'types/toDo';
import Editable from 'core/components/Editable';
import AutoCompletedInput from 'core/components/AutoComplete';
import Name from 'pages/Dashboard/components/Name';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';

type AssigneeUpdate = {
  assigneeUserId?: number;
};

export type EditableAssigneeProps = {
  toDo?: ToDo;
  handleChange: (update: AssigneeUpdate) => void;
  isUpdating: boolean;
  idleView?: ReactNode;
  className?: string;
  controlClassName?: string;
  popperClassName?: string;
  showEditIconOnHover?: boolean;
};

export default function EditableAssignee({
  toDo,
  handleChange,
  isUpdating,
  idleView,
  className,
  controlClassName,
  popperClassName,
  showEditIconOnHover = false,
}: EditableAssigneeProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: users } = useGetUsers({ onlyDoctors: false });
  const activeUsers = useMemo(
    () => users?.filter((user) => user.isActive && !user.isServiceAccount) ?? [],
    [users],
  );
  const assignee = toDo?.assignee ?? toDo?.assignedUsers?.[0] ?? null;
  const [value, setValue] = useState<UserDTO | null>(assignee);
  const assigneeSrc = useUserProfilePicturePath(value?.userId);

  useEffect(() => {
    setValue(assignee);
  }, [assignee]);

  return (
    <div
      role="button"
      tabIndex={-1}
      className={clsx(
        'flex min-w-0 flex-col gap-2',
        { 'group/assignee': showEditIconOnHover },
        className,
      )}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Editable
        readonly={!toDo || toDo.isAbandoned || isUpdating}
        idleView={idleView ?? (
          <Name
            {...(value?.formattedNames ?? {})}
            image={{ src: assigneeSrc }}
          />
        )}
        classes={{
          idleRoot: clsx(
            '!h-9 !min-w-0 !max-w-full !px-0 !border-0',
            { '!w-full': showEditIconOnHover },
          ),
          editableRoot: '!w-full !min-w-0',
          iconRoot: clsx({
            'opacity-0 transition-opacity group-hover/assignee:opacity-100 group-focus-within/assignee:opacity-100': showEditIconOnHover,
          }),
        }}
        showActions={false}
        isLoading={isUpdating}
        controlRef={inputRef as RefObject<HTMLElement>}
        tooltip="Update Assignee"
        onCancel={() => { setValue(assignee); }}
      >
        {(onSubmit, onCancel) => (
          <AutoCompletedInput<UserDTO>
            id={`to-do-assignee-${inputId}`}
            inputRef={inputRef}
            className={clsx('!bg-white', controlClassName ?? '!w-[300px]')}
            slotProps={{
              popper: {
                className: popperClassName,
                placement: 'bottom-start',
              },
            }}
            blurOnSelect
            openOnFocus
            disableClearable
            disabled={isUpdating}
            includeInputInList
            options={activeUsers}
            onClose={onCancel}
            getLabel={(user) => getDisplayName(user.formattedNames)}
            isOptionEqualToValue={(user, selected) => user.userId === (selected as UserDTO).userId}
            classes={{ inputRoot: '!pr-0' }}
            value={value}
            onChange={(_value: UserDTO | UserDTO[]) => {
              const value = _value as UserDTO;
              if (!isNil(value)) {
                setValue(value);
                handleChange({ assigneeUserId: value.userId });
              }
            }}
          />
        )}
      </Editable>
    </div>
  );
}
